/**
 * NewsManagement Component
 * 
 * Handles news management functionality including:
 * - Displaying news in a table format
 * - Creating, editing, and deleting news articles
 * - File management for news
 * - News status management
 * - WYSIWYG content editing
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Alert } from 'react-bootstrap';
import { 
    fetchAllNews, 
    createNews, 
    updateNews, 
    deleteNews, 
    removeNewsFile 
} from '../../services/api';
import NewsModal from './NewsModal';

const NewsManagement = () => {
    // State management
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showNewsModal, setShowNewsModal] = useState(false);
    const [editingNews, setEditingNews] = useState(null);
    const LOCALE = 'en-GB';
    const DATE_FORMAT_OPTIONS = { timeZone: 'Etc/GMT' };

    // News form data
    const [newsFormData, setNewsFormData] = useState({
        title: '',
        content: '',
        status: 'Draft',
        files: []
    });

    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');

    // Constants
    const newsStatuses = ['Draft', 'Published', 'Archived'];

    useEffect(() => {
        loadNews();
    }, []);

    /**
     * Loads all news from the API
     */
    const loadNews = async () => {
        try {
            setLoading(true);
            const data = await fetchAllNews();
            setNews(data);
            setError(null);
        } catch (error) {
            console.error('Error loading news:', error);
            setError('Failed to load news');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handles news form field changes
     * @param {Event} e - The change event
     */
    const handleNewsFormChange = (e) => {
        const { name, value, files } = e.target;
        
        if (name === 'files') {
            setNewsFormData(prev => ({
                ...prev,
                files: Array.from(files)
            }));
        } else {
            setNewsFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    /**
     * Handles news form submission
     * @param {Event} e - The form submit event
     */
    const handleNewsSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields client-side
        if (!newsFormData.title?.trim()) {
            alert('Title is required');
            return;
        }
        if (!newsFormData.content?.trim()) {
            alert('Content is required');
            return;
        }
        
        try {
            const formData = new FormData();
            
            // Append form fields
            Object.keys(newsFormData).forEach(key => {
                if (key === 'files') {
                    newsFormData.files.forEach(file => {
                        formData.append('files', file);
                    });
                } else {
                    const value = newsFormData[key] || '';
                    formData.append(key, value);
                }
            });

            if (editingNews) {
                // Update existing news
                await updateNews(editingNews._id, formData);
                alert('News updated successfully!');
            } else {
                // Create new news
                await createNews(formData);
                alert('News created successfully!');
            }

            setShowNewsModal(false);
            resetNewsForm();
            loadNews();
        } catch (error) {
            console.error('Error submitting news:', error);
            alert(`Failed to ${editingNews ? 'update' : 'create'} news: ${error.message}`);
        }
    };

    /**
     * Resets the news form to default values
     */
    const resetNewsForm = () => {
        setNewsFormData({
            title: '',
            content: '',
            status: 'Draft',
            files: []
        });
        setEditingNews(null);
    };

    /**
     * Prepares news for editing
     * @param {Object} newsItem - The news item to edit
     */
    const handleEditNews = (newsItem) => {
        setEditingNews(newsItem);
        setNewsFormData({
            title: newsItem.title || '',
            content: newsItem.content || '',
            status: newsItem.status || 'Draft',
            files: []
        });
        setShowNewsModal(true);
    };

    /**
     * Deletes a news item
     * @param {string} newsId - The ID of the news item to delete
     */
    const handleDeleteNews = async (newsId) => {
        if (window.confirm('Are you sure you want to delete this news item?')) {
            try {
                await deleteNews(newsId);
                alert('News deleted successfully');
                loadNews();
            } catch (error) {
                console.error('Error deleting news:', error);
                alert('Failed to delete news');
            }
        }
    };

    /**
     * Gets the appropriate icon for a file type
     * @param {string} mimetype - The file MIME type
     * @returns {string} Bootstrap icon class
     */
    const getFileIcon = (mimetype) => {
        if (mimetype?.includes('pdf')) return 'bi-file-earmark-pdf';
        if (mimetype?.includes('image')) return 'bi-file-earmark-image';
        if (mimetype?.includes('word') || mimetype?.includes('document')) return 'bi-file-earmark-word';
        if (mimetype?.includes('excel') || mimetype?.includes('spreadsheet')) return 'bi-file-earmark-excel';
        return 'bi-file-earmark';
    };

    /**
     * Deletes a news file
     * @param {string} newsId - The ID of the news
     * @param {string} filename - The filename to delete
     */
    const handleDeleteFile = async (newsId, filename) => {
        if (window.confirm('Are you sure you want to delete this file?')) {
            try {
                await removeNewsFile(newsId, filename);
                
                // Update the news list
                await loadNews();
                
                // If we're editing this news, update the editing state immediately
                if (editingNews && editingNews._id === newsId) {
                    const updatedNewsList = await fetchAllNews();
                    const updatedNews = updatedNewsList.find(n => n._id === newsId);
                    if (updatedNews) {
                        setEditingNews(updatedNews);
                    }
                }
                
                alert('File deleted successfully');
            } catch (error) {
                console.error('Error deleting file:', error);
                alert('Failed to delete file');
            }
        }
    };

    /**
     * Gets badge color for news status
     * @param {string} status - The news status
     * @returns {string} Bootstrap badge class
     */
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Published': return 'bg-success';
            case 'Draft': return 'bg-warning text-dark';
            case 'Archived': return 'bg-secondary';
            default: return 'bg-primary';
        }
    };

    // Filter news based on search term and status
    const filteredNews = news.filter(newsItem => {
        const matchesSearch = newsItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             newsItem.content?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'All' || newsItem.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    if (loading) return <div className="text-center p-4">Loading news...</div>;
    if (error) return <Alert variant="danger">{error}</Alert>;

    return (
        <Container fluid>
            <Row>
                <Col>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">
                                <i className="bi bi-newspaper me-2"></i>
                                News Management
                            </h4>
                            <Button 
                                variant="primary" 
                                onClick={() => {
                                    resetNewsForm();
                                    setShowNewsModal(true);
                                }}
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                Add News
                            </Button>
                        </Card.Header>

                        <Card.Body>
                            {/* Search and Filter Controls */}
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search news..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="All">All Status</option>
                                        {newsStatuses.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <div className="text-muted">
                                        Showing {filteredNews.length} of {news.length} news items
                                    </div>
                                </Col>
                            </Row>

                            {/* News Table */}
                            <Table responsive striped hover>
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Status</th>
                                        <th>Files</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredNews.map((newsItem) => (
                                        <tr key={newsItem._id}>
                                            <td>
                                                <div>
                                                    <strong>{newsItem.title}</strong>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge ${getStatusBadgeColor(newsItem.status)}`}>
                                                    {newsItem.status}
                                                </span>
                                            </td>
                                            <td>
                                                {newsItem.files && newsItem.files.length > 0 ? (
                                                    <span className="badge bg-info">
                                                        {newsItem.files.length} file(s)
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">No files</span>
                                                )}
                                            </td>
                                            <td>
                                                <small>
                                                    {new Date(newsItem.createdAt).toLocaleDateString(LOCALE, DATE_FORMAT_OPTIONS)}
                                                </small>
                                            </td>
                                            <td>
                                                <div className="btn-group btn-group-sm">
                                                    <button
                                                        className="btn btn-outline-warning"
                                                        onClick={() => handleEditNews(newsItem)}
                                                        title="Edit News"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-danger"
                                                        onClick={() => handleDeleteNews(newsItem._id)}
                                                        title="Delete News"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {filteredNews.length === 0 && (
                                <div className="text-center text-muted py-4">
                                    No news items found
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* News Modal */}
            {showNewsModal && (
                <NewsModal
                    show={showNewsModal}
                    onHide={() => setShowNewsModal(false)}
                    editingNews={editingNews}
                    newsFormData={newsFormData}
                    onFormChange={handleNewsFormChange}
                    onSubmit={handleNewsSubmit}
                    onReset={resetNewsForm}
                    newsStatuses={newsStatuses}
                    getFileIcon={getFileIcon}
                    onDeleteFile={handleDeleteFile}
                />
            )}
        </Container>
    );
};

export default NewsManagement;